import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useRecoilValue } from "recoil";
import { isEmpty } from "lodash";
import { useQuery } from "Hooks/index";
import { Col, Row } from "antd";
import { Tabs } from "@life-line-lab/components-react";
import StatisticBox from "Components/shared/statistic-box";
import ModuleTitleBar from "Components/shared/module-title-bar/module-title-bar";
import useSession from "Hooks/session.hook";
import "./parcel-tracking.scss";
import PickupList from "Components/delivery-management/parcel-tracking-tabs/pickup-list";
import CustomerDropOff from "Components/delivery-management/parcel-tracking-tabs/dropoff-list";
import TrackingSorting from "Components/delivery-management/parcel-tracking-tabs/sorting-list";
import LastMileList from "Components/delivery-management/parcel-tracking-tabs/last-mile-list";
import CompletedList from "Components/delivery-management/parcel-tracking-tabs/completed-list";
import EnrouteTabs from "Components/delivery-management/parcel-tracking-tabs/enroute-tabs";
import ErrorBoundary from "antd/lib/alert/ErrorBoundary";
import { GlobalFilterAtom } from "Recoil/global_filter.atom";
import useParcelTrackingFilters from "./use-parcel-tracking";
import useCheckUserRole from "Hooks/check-user-role.hook";
import { UserRoleToAssign } from "Types/user.types";
import useCorporateType from "Hooks/corporate-type.hook";

const ParcelTracking = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const query = useQuery();
  const { isRole } = useCheckUserRole();
  const { isCustomer } = useCorporateType();
  const tabKey = query.get("tabKey") || "0";
  const { roleTypes } = useSession();
  const globalFilterState = useRecoilValue(GlobalFilterAtom);
  const { parcelStates, parcelFunctions, loadingMechanism, paginationProps } =
    useParcelTrackingFilters({ tabKey, isCustomer });

  const {
    initialFilters,
    statisticsBoxes,
    filterObj,
    setFilterObj,
    vehicleOptions,
    customerOptions,
    driverOptions,
    sortingHubOptions,
    vehicleTypeOptions,
    parcelStatistics,
    setParcelStatistics,
    allOrderList,
    tabActiveKey,
    setTabActiveKey,
  } = parcelStates || {};

  const {
    getStatCount,
    handleAssignDriver,
    handleCombinePickUpTask,
    handleRefreshData,
    handleAPIListingByTabs,
  } = parcelFunctions || {};

  const { paginationComponent, itemPerPage, queryParams, setCurrentListCount } =
    paginationProps || {};

  useEffect(() => {
    if (
      !isEmpty(globalFilterState) &&
      globalFilterState?.selectedOrgIDs?.length
    ) {
      handleRefreshData();
    }
  }, [globalFilterState]);

  const onFilter = (filterInput) => {
    if (isEmpty(filterInput)) {
      setFilterObj(initialFilters);
      return;
    }

    const filterValue = {
      ...filterObj,
      orgID: filterInput.customersList ? filterInput.customersList : "",
      vehicleType: filterInput.vehicleType || null,
      driverID: filterInput.driver || null,
      orFields: filterInput.orFields || null,
      unassignedDriver: filterInput.unassignedDriver || null,
      unassignedVehicleType: filterInput.unassignedVehicleType || null,
    };

    setFilterObj(filterValue);
  };

  useEffect(() => {
    handleAPIListingByTabs();
    // getStatCount();
  }, [filterObj]);

  useEffect(() => {
    if (roleTypes == "CUSTOMER-SUPER_ADMIN") {
      statisticsBoxes.splice(2, 2);
    }
    setParcelStatistics(statisticsBoxes);
  }, []);

  useEffect(() => {
    handleRefreshData();
  }, [queryParams.offset, itemPerPage, tabActiveKey]);

  const tabs = () => {
    const allTabs = [
      {
        tabKey: "0",
        label: t("tab.diolkoPickup"),
        component: (
          //TODO Refactoring: these components should be placed in a its own page, right now it is in component folder - Ashiq's suggestion
          <PickupList
            onFilter={onFilter}
            vehicleOptions={vehicleOptions}
            vehicleTypeOptions={vehicleTypeOptions}
            customerOptions={customerOptions}
            driverOptions={driverOptions}
            allOrderList={allOrderList}
            onAssignDriver={handleAssignDriver}
            onCombineTask={handleCombinePickUpTask}
            loadingMechanism={loadingMechanism}
            handleRefresh={handleRefreshData}
          />
        ),
      },
      {
        tabKey: "1",
        label: t("tab.customerDropOff"),
        component: (
          <CustomerDropOff
            onFilter={onFilter}
            customerOptions={customerOptions}
            allOrderList={allOrderList}
            loadingMechanism={loadingMechanism}
            handleRefresh={handleRefreshData}
            filterState={filterObj}
          />
        ),
      },
      {
        tabKey: "2",
        label: t("tab.sorting"),
        component: (
          <TrackingSorting
            customerOptions={customerOptions}
            sortingHubOptions={sortingHubOptions}
            vehicleOptions={vehicleOptions}
            loadingMechanism={loadingMechanism}
            queryParams={queryParams}
            itemPerPage={itemPerPage}
            setCurrentListCount={setCurrentListCount}
          />
        ),
      },
      {
        tabKey: "3",
        label: t("tab.enroute"),
        component: (
          <EnrouteTabs
            loadingMechanism={loadingMechanism}
            queryParams={queryParams}
            itemPerPage={itemPerPage}
            setCurrentListCount={setCurrentListCount}
          />
        ),
      },
      {
        tabKey: "4",
        label: t("tab.lastMile"),
        component: (
          <LastMileList
            loadingMechanism={loadingMechanism}
            queryParams={queryParams}
            itemPerPage={itemPerPage}
            setCurrentListCount={setCurrentListCount}
            sortingHubOptions={sortingHubOptions}
            vehicleOptions={vehicleOptions}
          />
        ),
      },
      {
        tabKey: "5",
        label: t("tab.completed"),
        component: (
          <CompletedList
            sortingHubOptions={sortingHubOptions}
            queryParams={queryParams}
            itemPerPage={itemPerPage}
          />
        ),
      },
    ];

    if (isRole([UserRoleToAssign.PUBLIC_TRANSPORT_OPERATIONAL_USER]))
      return allTabs.filter((item) => ["3"].includes(item.tabKey));

    if (roleTypes.includes("CUSTOMER-SUPER_ADMIN"))
      return allTabs.filter((item) =>
        ["0", "1", "2", "4", "5"].includes(item.tabKey),
      );

    if (isCustomer)
      return allTabs.filter((item) =>
        ["0", "1", "2", "3", "4"].includes(item.tabKey),
      );

    return allTabs;
  };

  const handleStatClick = (key: string) => {
    handleTabClick(key);
  };

  const handleTabClick = (key) => {
    let path = "";

    if (key === "0") {
      path = `/delivery-management/parcel-tracking`;
    } else {
      path = `/delivery-management/parcel-tracking?tabKey=${key}`;
    }

    // setFilterObj && setFilterObj(initialFilters);
    setTabActiveKey(key);
    history.push(path);
  };

  return (
    <div>
      <div className="parcel-tracking-list">
        <Helmet>
          <title>{t("title.deliveryManagement")}</title>
        </Helmet>
        <ModuleTitleBar titleText={t("title.deliveryManagement")} />
        <>
          <Row className="mt-20">
            {/* STATISTICS BOXES */}
            <Col span={24}>
              <div className="tracking-list-statistics-group">
                {parcelStatistics.map((parcelStat, index) => {
                  return (
                    <StatisticBox
                      key={index}
                      statObj={parcelStat}
                      count={parcelStat.count}
                      context={""}
                      onClick={handleStatClick}
                      icon={parcelStat.icon}
                      iconSrc={parcelStat.iconSrc}
                    />
                  );
                })}
              </div>
            </Col>
          </Row>
          <ErrorBoundary>
            <Tabs
              destroyInactiveTabPane={true}
              onTabClick={handleTabClick}
              defaultActiveKey="0"
              activeKey={tabActiveKey}
              tabBarGutter={25}
              tabs={tabs() /*ctrl click tabs to see its components */}
            />
          </ErrorBoundary>
          <div className="my-30">{paginationComponent}</div>
        </>
      </div>
    </div>
  );
};

export default ParcelTracking;
